import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import Avatar from '@base/Avatar/Avatar';
import P2 from '@base/P2/P2';
import Divider from '@base/Divider/Divider';
import CardHeader from './CardHeader';
import Card from './Card';

const CardWrapper = styled.div``;

const Title = styled(P2)`
  font-weight: 600;
  font-size: 14px;
  line-height: 150%;
  color: var(--color-text-primary);
`;

const StyledCard = styled(Card)`
  margin-top: 16px;
`;

const CardInfo = styled.div`
  display: flex;
  align-items: center;
`;

const SubTitle = styled.span`
  font-size: 12px;
  line-height: 150%;
  color: var(--color-text-secondary);
  margin-top: 4px;
`;

const UNDivider = styled(Divider)`
  margin: 16px 0;
  background-color: var(--color-shadow);
`;

const WrappedDiv = styled.div`
  margin-left: 16px;
`;

const CardDetails = styled.div``;

const ToppersWrapper = styled.div`
  display: flex;
  width: 100%;
  flex-wrap: wrap;
  background: radial-gradient(
    51.51% 132.57% at 48.61% 143.75%,
    var(--color-shadow) 0%,
    var(--color-base-1) 100%
  );
  padding: 24px;
  justify-content: space-between;
  align-items: flex-end;
`;

const AvatarWrapper = styled.div`
  position: relative;
  width: min-content;
`;

const posColor = {
  0: 'var(--color-i-yellow-1)',
  1: 'var(--color-i-orange-2)',
  2: 'var(--color-i-turquoise-1)'
};

const Position = styled.div`
  background-color: ${({ pos }) => posColor[pos]};
  border: 2px solid var(--color-i-white);
  border-radius: 9px;
  font-size: 12px;
  line-height: 14px;
  color: var(--color-i-white);
  text-shadow: 0px 0px 8px rgba(0, 0, 0, 0.32);
  text-align: center;
  position: absolute;
  width: 30px;
  height: 18px;
  left: calc(50% - 15px);
  bottom: -5px;
`;

const Name = styled.p`
  font-weight: 600;
  font-size: 14px;
  line-height: 150%;
  color: var(--color-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: center;
  width: 100%;
  margin-top: 4px;
`;

const TopperWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-grow: 1;
  flex-basis: 0;
  min-width: 0;
`;

const MarksInfo = styled.div`
  display: flex;
`;

const TotalMarks = styled.span`
  color: var(--color-text-secondary);
  font-size: 14px;
  line-height: 150%;
`;

const Correct = styled.span`
  font-weight: bold;
  font-size: 14px;
  line-height: 150%;
  color: var(--color-i-green);
`;

const TimeTaken = styled.span`
  font-size: 10px;
  line-height: 12px;
  color: var(--color-text-primary);
  opacity: 0.5;
  background: var(--color-shadow);
  border-radius: 8px;
  padding: 0 4px;
  margin-left: 8px;
  display: inline-flex;
  align-items: center;
`;

const OthersWrapper = styled.div`
  padding-top: 8px;
  padding-bottom: 16px;
`;

const OtherWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-top: 16px;
`;

const Rank = styled.span`
  margin-right: 8px;
  font-size: 14px;
  line-height: 150%;
  color: var(--color-text-primary);
`;
const OtherName = styled.span`
  margin: 0 8px;
  font-size: 14px;
  line-height: 150%;
  color: var(--color-text-primary);
`;

const Marks = ({ learner }) => (
  <MarksInfo>
    <Correct>{learner.correct_answer_count}</Correct>
    <TotalMarks>/</TotalMarks>
    <TotalMarks>{learner.total_questions_count}</TotalMarks>
    <TimeTaken>{`${learner.total_time_taken}s`}</TimeTaken>
  </MarksInfo>
);

const StyledAvatar = styled(Avatar)`
  cursor: auto;
`;

const Topper = ({ topper, size, pos }) => (
  <TopperWrapper>
    <AvatarWrapper>
      <StyledAvatar circular src={topper.avatar} size={size || '48px'} />
      <Position pos={pos}>{topper?.rank}</Position>
    </AvatarWrapper>
    <Name>{`${topper.first_name} ${topper.last_name}`}</Name>
    <Marks learner={topper} />
  </TopperWrapper>
);

const Toppers = ({ toppers }) => (
  <ToppersWrapper>
    <Topper pos={1} topper={toppers?.[1]} />
    <Topper pos={0} size="64px" topper={toppers?.[0]} />
    <Topper pos={2} topper={toppers?.[2]} />
  </ToppersWrapper>
);

const Other = ({ other }) => (
  <OtherWrapper>
    <Rank>{`${other?.rank}.`}</Rank>
    <StyledAvatar circular src={other.avatar} size="24px" />
    <OtherName>{`${other.first_name} ${other.last_name} -`}</OtherName>
    <Marks learner={other} />
  </OtherWrapper>
);

const Others = ({ others }) => (
  <OthersWrapper>
    {others.map((other) => (
      <Other key={other.uid} other={other} />
    ))}
  </OthersWrapper>
);

const Leaderboard = ({ story, batch }) => {
  const { created_at, object, author } = story;
  if (!object.poll_leaderboard) return null;
  const lesson = `Lesson ${object?.rank}`;
  const toppers = object.poll_leaderboard.slice(0, 3);
  const others = object.poll_leaderboard.slice(3);
  const avatar = author.avatar_v1 || author.avatar;
  return (
    <CardWrapper>
      <CardHeader
        message="Poll Leaderboards"
        created_at={created_at}
        img="poll.svg"
        subtitle={batch && object.programme.name}
      />
      <StyledCard>
        <CardInfo>
          <StyledAvatar src={avatar} size="48px" />
          <WrappedDiv>
            <Title lineClamp={1}>{object.name}</Title>
            <SubTitle>{lesson}</SubTitle>
          </WrappedDiv>
        </CardInfo>
        <UNDivider />
        <CardDetails>
          <Toppers toppers={toppers} />
          <Others others={others} />
        </CardDetails>
      </StyledCard>
    </CardWrapper>
  );
};

export default Leaderboard;

Leaderboard.propTypes = {
  story: PropTypes.objectOf(PropTypes.any).isRequired,
  batch: PropTypes.bool
};

Leaderboard.defaultProps = {
  batch: false
};

Others.propTypes = {
  others: PropTypes.arrayOf(PropTypes.any).isRequired
};

Other.propTypes = {
  other: PropTypes.objectOf(PropTypes.any).isRequired
};

Topper.propTypes = {
  topper: PropTypes.objectOf(PropTypes.any).isRequired,
  size: PropTypes.string,
  pos: PropTypes.number.isRequired
};

Topper.defaultProps = {
  size: null
};

Toppers.propTypes = {
  toppers: PropTypes.arrayOf(PropTypes.any).isRequired
};

Marks.propTypes = {
  learner: PropTypes.objectOf(PropTypes.any).isRequired
};
